import { type FC, useContext, useMemo } from "react";

import { Flex } from "@mantine/core";
import { Alert, Button, Modal } from "@snapchat/snap-design-system";

import {
    ExclusiveLensStatus,
    LensCreatorPayoutEligibility,
    type MyLensesLens,
    SurfaceType,
    Visibility,
} from "../../gql-generated/types";
// The real publish modal's own look: importing its style exports (rather than copying them) keeps
// the two modals restyling together — this modal is a thin web-lens twin, not a fork.
import {
    LensNameHeading,
    VisibilityHeading,
    VisibilityOptionsWrapper,
    VisibilitySectionWrapper,
    alertWithBackground,
    visibilityOptionWithBorder,
} from "../../lens/components/shared/PublishModal.styles";
import {
    Cancel,
    ErrorTitle,
    LearnMoreText,
    LensPlusOnlyVisibilityOptionsNotice,
    PublicationWarning,
    PublicationWarningText,
    PublishModalTitle,
    UpdateModalTitle,
    VisibilityTitle,
} from "../../lens/components/shared/messages.generated";
import { usePublishSubmissionQuotaGuard } from "../../lens/components/shared/usePublishSubmissionQuotaGuard";
import { LensPreview } from "../../lens-preview/components/LensPreview";
import { type LensPreviewStateHook } from "../../lens-preview/hooks";
import { VISIBILITY_OPTION_MAP } from "../../lens-visibility/components";
import { type LensVisibilityConfig, makeExclusiveLensVisibilityConfig } from "../../lens-visibility/hooks";
import { UserInfoContext } from "../../shared/api/contexts";
import { Flexbox } from "../../shared/components";
import { SubmissionQuotaContext } from "../../shared/components/submission-quota/SubmissionQuotaContext";
import { SubmissionQuotaInline } from "../../shared/components/submission-quota/SubmissionQuotaInline";
import { type ModalProps } from "../../shared/hooks";
import { useSubmissionQuotas } from "../../shared/hooks/submission-quotas";
import { type LensPreviewMediaItem } from "../../shared/state";
import { MonetizationSection } from "../../submit/components/shared/MonetizationSection";

import styles from "./WebLensPage.module.css";
import { UpdateRailHelper } from "./messages.generated";

const LEARN_MORE_URL = "https://businesshelp.snapchat.com/s/article/lens-profile";
const SNAPCHAT_SURFACE = [SurfaceType.SurfaceTypeSnapchat] as const;

type WebLensPublishModalProps = ModalProps & {
    lensName: string;
    lensForUpdate?: Pick<
        MyLensesLens,
        "name" | "distributionsList" | "lensCreatorPayoutEligibility" | "exclusiveLensStatus"
    >;

    visibilityConfig: LensVisibilityConfig;
    visibility: Visibility;
    onVisibilityChange: (visibility: Visibility) => void;

    // Lens Creator Rewards — the same shared controls as the regular PublishModal, rendered behind
    // the caller's MyLensesCreatorPayout gate.
    isMyLensesCreatorPayoutEnabled: boolean;
    isCreatorPayoutEnrolled: boolean;
    onCreatorPayoutEnrolledChange: (isEnrolled: boolean) => void;
    isExclusiveLensChecked: boolean;
    onExclusiveLensChange?: (isExclusive: boolean) => void;

    previewMediaItems: LensPreviewMediaItem[];
    lensPreviewProps: Omit<LensPreviewStateHook, "uploadFileLoading">;
    helperMessage?: React.ReactNode;

    onConfirm: () => void;
    confirmLabel: React.ReactNode;
    confirmIsPending: boolean;
    confirmIsDisabled: boolean;
    errorMessage?: string | null;
};

/**
 * Shares Lens Pro's presentation and rewards controls without depending on its submission atoms.
 * Existing Lenses do not consume new-publication quotas.
 */
export const WebLensPublishModal: FC<WebLensPublishModalProps> = ({
    visible,
    onCancel,
    lensName,
    lensForUpdate,
    visibilityConfig,
    visibility,
    onVisibilityChange,
    isMyLensesCreatorPayoutEnabled,
    isCreatorPayoutEnrolled,
    onCreatorPayoutEnrolledChange,
    isExclusiveLensChecked,
    onExclusiveLensChange,
    previewMediaItems,
    lensPreviewProps,
    helperMessage,
    onConfirm,
    confirmLabel,
    confirmIsPending,
    confirmIsDisabled,
    errorMessage,
}) => {
    const userInfo = useContext(UserInfoContext);
    const username = userInfo?.data?.getUserProfile.userDisplayName ?? "";
    const requiresSubmissionQuota = !lensForUpdate && visibility !== Visibility.VisibilityDontPublishToSnapchat;
    const isMonetizationDisabled =
        visibility === Visibility.VisibilityDontPublishToSnapchat ||
        Boolean(
            lensForUpdate &&
                lensForUpdate.lensCreatorPayoutEligibility !==
                    LensCreatorPayoutEligibility.LensCreatorPayoutEligibilityUnset,
        );

    // Same quota wiring as the real modal: the footer shows the quota the submission will draw
    // from (regular, or muted-regular while Lens+ is checked — the Lens+ quota then shows inside
    // the monetization block), and the confirm re-checks against a fresh read before publishing.
    const { regular, lensPlus, refetch } = useSubmissionQuotas();
    const {
        activeSubmissionQuota,
        activeSubmissionQuotaContext,
        isChecking: isQuotaCheckPending,
        isBlocked: isSubmissionQuotaExhausted,
        checkBeforePublish,
    } = usePublishSubmissionQuotaGuard({
        requiresSubmissionQuota,
        usesLensPlusQuota: isExclusiveLensChecked,
        regularSubmissionQuota: regular,
        lensPlusSubmissionQuota: lensPlus,
        refetchSubmissionQuotas: refetch,
        isModalVisible: visible,
    });
    const isRegularQuotaMuted = isExclusiveLensChecked;
    const displayedQuota = isRegularQuotaMuted ? regular : activeSubmissionQuota;
    const displayedQuotaContext = isRegularQuotaMuted ? SubmissionQuotaContext.Regular : activeSubmissionQuotaContext;

    const isConfirmPending = confirmIsPending || isQuotaCheckPending;
    const isConfirmDisabled = confirmIsDisabled || isSubmissionQuotaExhausted;
    const enhancedVisibilityConfig = useMemo(
        () =>
            isExclusiveLensChecked
                ? makeExclusiveLensVisibilityConfig(visibilityConfig, <LensPlusOnlyVisibilityOptionsNotice />)
                : visibilityConfig,
        [isExclusiveLensChecked, visibilityConfig],
    );

    const handleConfirm = async () => {
        if (isConfirmDisabled || isConfirmPending) {
            return;
        }
        if (requiresSubmissionQuota) {
            const shouldBlockPublication = await checkBeforePublish();
            if (shouldBlockPublication) {
                return;
            }
        }
        onConfirm();
    };

    return (
        <Modal
            data-testid="web-lens-publish-modal"
            title={lensForUpdate ? <UpdateModalTitle /> : <PublishModalTitle />}
            visible={visible}
            onCancel={onCancel}
            width={760}
            footer={
                <div className={styles.modalFooter}>
                    <div className={styles.modalFooterQuota}>
                        {requiresSubmissionQuota ? (
                            <SubmissionQuotaInline
                                quota={displayedQuota}
                                context={displayedQuotaContext}
                                isMuted={isRegularQuotaMuted}
                            />
                        ) : null}
                    </div>
                    <div className={styles.modalFooterActions}>
                        <Button onClick={onCancel} isDisabled={isConfirmPending}>
                            <Cancel />
                        </Button>
                        <Button
                            type="primary"
                            onClick={() => void handleConfirm()}
                            isPending={isConfirmPending}
                            isDisabled={isConfirmDisabled}
                            data-testid="web-lens-publish-confirm"
                        >
                            {confirmLabel}
                        </Button>
                    </div>
                </div>
            }
        >
            <Flexbox justifyContent="space-between">
                <Flexbox flexDirection="column" alignItems="center">
                    <LensPreview
                        {...lensPreviewProps}
                        isReadOnly={true}
                        isLargePreview={true}
                        surfaceType={SNAPCHAT_SURFACE}
                    />
                    <LensNameHeading>{lensName}</LensNameHeading>
                </Flexbox>
                <VisibilitySectionWrapper>
                    <Flex direction="column" gap="xs">
                        <Alert
                            className={alertWithBackground}
                            status="warning"
                            title={lensForUpdate ? <UpdateModalTitle /> : <PublicationWarning username={username} />}
                            message={
                                lensForUpdate ? (
                                    helperMessage ?? <UpdateRailHelper />
                                ) : (
                                    <PublicationWarningText
                                        username={username}
                                        learnMore={
                                            <a href={LEARN_MORE_URL} target="_blank" rel="noreferrer">
                                                <LearnMoreText />
                                            </a>
                                        }
                                    />
                                )
                            }
                        />
                    </Flex>

                    <VisibilityOptionsWrapper>
                        <VisibilityHeading>
                            <VisibilityTitle />
                        </VisibilityHeading>
                        {enhancedVisibilityConfig.communityDistributionOptions?.map((option) => {
                            const VisibilityOptionCard = VISIBILITY_OPTION_MAP[option];
                            const disabledVisibilityOptionConfig = enhancedVisibilityConfig.disabledOptions?.[option];
                            return (
                                <VisibilityOptionCard
                                    className={visibilityOptionWithBorder}
                                    key={option}
                                    setVisibility={() => {
                                        if (!isConfirmPending && !disabledVisibilityOptionConfig) {
                                            onVisibilityChange(option);
                                        }
                                    }}
                                    selected={option === visibility}
                                    disabledVisibilityOptionConfig={disabledVisibilityOptionConfig}
                                />
                            );
                        })}
                    </VisibilityOptionsWrapper>
                    <Flex gap="md" direction="column">
                        {isMyLensesCreatorPayoutEnabled ? (
                            <MonetizationSection
                                previewMediaItems={previewMediaItems}
                                lensCreatorPayoutEnrolled={isCreatorPayoutEnrolled}
                                setLensCreatorPayoutEnrolled={onCreatorPayoutEnrolledChange}
                                lensForUpdate={lensForUpdate}
                                exclusiveLensStatus={
                                    lensForUpdate?.exclusiveLensStatus ?? ExclusiveLensStatus.ExclusiveLensStatusUnset
                                }
                                onExclusiveLensChange={onExclusiveLensChange}
                                canToggleExclusiveLens={!lensForUpdate}
                                disabled={isConfirmPending || isMonetizationDisabled}
                                // Web lenses are Snapchat-surface only, the same condition the
                                // submission flow derives per lens.
                                isExclusiveLensFeatureEnabled={true}
                                isMyLensesCreatorPayoutEnabled={isMyLensesCreatorPayoutEnabled}
                                isExclusiveLensChecked={isExclusiveLensChecked}
                                visibility={visibility}
                                lensPlusSubmissionQuota={requiresSubmissionQuota ? lensPlus : null}
                            />
                        ) : null}

                        {errorMessage ? (
                            <Alert status="error" title={<ErrorTitle />} description={errorMessage} />
                        ) : null}
                    </Flex>
                </VisibilitySectionWrapper>
            </Flexbox>
        </Modal>
    );
};
